import React, { useState, useMemo } from 'react';
import { ArrowLeft, Search, MapPin, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { Location } from '../../types';
import { BRAND_ICONS, normalizeBrandName, Brand, BRANDS, LOCATION_ONLY_BRANDS } from '../../utils/brands';
import { calculateDistance, formatDistance } from '../../utils/distance';
import './BrandLocationsView.css';

interface BrandLocationsViewProps {
  brand: string;
  locations: Array<Location & { distance?: number }>;
  allLocations?: Record<string, Location>;
  userLocation?: [number, number] | null;
  referenceLocation?: [number, number] | null; // Location from Home2 (selected city or current location)
  onBack: () => void;
  onLocationClick: (location: Location) => void;
  onShopClick?: (locationName: string, shopName: string, location: Location) => void;
}

const BrandLocationsView: React.FC<BrandLocationsViewProps> = ({
  brand,
  locations,
  allLocations = {},
  userLocation,
  referenceLocation,
  onBack,
  onLocationClick,
  onShopClick,
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  // Get all shops and locations for this brand - show shops as individual items
  interface BrandItem {
    type: 'location' | 'shop';
    location: Location & { distance?: number };
    shopName?: string;
    shopKey?: string;
    shopData?: any;
    displayName: string;
    distance?: number;
  }

  const allBrandItems = useMemo(() => {
    if (!allLocations) return [];

    const items: BrandItem[] = [];
    // Type assertion: brand should be a valid Brand type, but we'll handle it safely
    const brandLower = BRANDS.includes(brand as Brand) 
      ? normalizeBrandName(brand as Brand).toLowerCase()
      : brand.toLowerCase();
    // Use referenceLocation (from Home2) if available, otherwise fall back to userLocation
    const currentLocation = referenceLocation || userLocation || null;
    const isLocationOnlyBrand = LOCATION_ONLY_BRANDS.includes(brand as Brand);

    Object.entries(allLocations).forEach(([key, loc]) => {
      if (!loc || !loc.latitude || !loc.longitude) return;

      const locationWithName = {
        ...loc,
        location: loc.location || key,
        _id: loc._id || key,
      };

      const locationNameLower = (locationWithName.location || '').toLowerCase();
      let distance: number | undefined;
      if (currentLocation) {
        distance = calculateDistance(
          currentLocation[0],
          currentLocation[1],
          locationWithName.latitude,
          locationWithName.longitude
        );
      }

      // Exclude "adams apple" from Apple brand search
      if (brand === 'Apple' && locationNameLower.includes('adams apple')) {
        return;
      }

      // Check if location name matches brand (e.g., "Westfield Bondi Junction")
      if (locationNameLower.includes(brandLower)) {
        items.push({
          type: 'location',
          location: locationWithName,
          displayName: locationWithName.location || locationWithName._id || key,
          distance,
        });
      }

      // For non-location-only brands, check shops within locations and add them as separate items
      if (!isLocationOnlyBrand && locationWithName.shops) {
        // Handle array format (array of shop name strings)
        if (Array.isArray(locationWithName.shops)) {
          locationWithName.shops.forEach((shop: any) => {
            if (!shop) return;
            // If shop is a string, use it directly
            const shopName = typeof shop === 'string' 
              ? shop
              : (shop.name || shop.storeName || shop.title || shop.shopName || '');
            const shopNameLower = shopName.toLowerCase();
            
            // Exclude "adams apple" from Apple brand search
            if (brand === 'Apple' && shopNameLower.includes('adams apple')) {
              return;
            }
            
            if (shopNameLower.includes(brandLower) || shopNameLower === brandLower) {
              items.push({
                type: 'shop',
                location: locationWithName,
                shopName: shopName,
                shopKey: typeof shop === 'string' ? shopName : undefined,
                shopData: typeof shop === 'object' ? shop : undefined,
                displayName: shopName,
                distance,
              });
            }
          });
        } 
        // Handle object format (object with shop keys and shop data)
        else if (typeof locationWithName.shops === 'object') {
          Object.entries(locationWithName.shops).forEach(([shopKey, shop]: [string, any]) => {
            if (!shop) return;
            
            // Get shop name from various possible fields, or use the key
            const shopName = typeof shop === 'string'
              ? shop
              : (shop.name || shop.storeName || shop.title || shop.shopName || shopKey || '');
            const shopNameLower = shopName.toLowerCase();
            
            // Exclude "adams apple" from Apple brand search
            if (brand === 'Apple' && shopNameLower.includes('adams apple')) {
              return;
            }
            
            // Match shop name against normalized brand name
            if (shopNameLower.includes(brandLower) || shopNameLower === brandLower) {
              items.push({
                type: 'shop',
                location: locationWithName,
                shopName: shopName,
                shopKey: shopKey,
                shopData: typeof shop === 'object' ? shop : undefined,
                displayName: shopName,
                distance,
              });
            }
          });
        }
      }
    });

    // Sort by distance if available, otherwise by name
    items.sort((a, b) => {
      if (a.distance !== undefined && b.distance !== undefined) {
        return a.distance - b.distance;
      }
      if (a.distance !== undefined) return -1;
      if (b.distance !== undefined) return 1;
      return a.displayName.localeCompare(b.displayName);
    });

    return items;
  }, [allLocations, brand, userLocation, referenceLocation]);

  // Filter items based on search query
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return allBrandItems;

    const query = searchQuery.toLowerCase();
    return allBrandItems.filter((item) => {
      const displayName = item.displayName.toLowerCase();
      const locationName = (item.location.location || item.location._id || '').toLowerCase();
      const address = (item.location as any).address?.toLowerCase() || '';
      return displayName.includes(query) || locationName.includes(query) || address.includes(query);
    });
  }, [allBrandItems, searchQuery]);

  const BrandIcon = BRAND_ICONS[brand as keyof typeof BRAND_ICONS] || MapPin;

  const handleItemClick = (item: BrandItem) => {
    if (item.type === 'shop' && onShopClick && item.shopName) {
      // Handle shop click - trigger shop modal
      onShopClick(item.location.location || item.location._id || '', item.shopName, item.location);
    } else {
      // Handle location click
      onLocationClick(item.location);
    }
  };

  return (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'spring', damping: 25, stiffness: 300 }}
      className="brand-locations-view"
    >
      {/* Header */}
      <div className="brand-locations-header">
        <button className="brand-locations-back-button" onClick={onBack}>
          <ArrowLeft size={24} />
        </button>
        <div className="brand-locations-title-section">
          <div className="brand-locations-icon">
            <BrandIcon size={24} />
          </div>
          <h1 className="brand-locations-title">{brand}</h1>
        </div>
        <div style={{ width: 40 }} /> {/* Spacer for centering */}
      </div>

      {/* Search Bar */}
      <div className="brand-locations-search-container">
        <div className="brand-locations-search-bar">
          <Search size={20} className="brand-locations-search-icon" />
          <input
            type="text"
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="brand-locations-search-input"
          />
        </div>
      </div>

      {/* Items List (shops and locations) */}
      <div className="brand-locations-list">
        {filteredItems.length === 0 ? (
          <div className="brand-locations-empty">
            <p>No locations found</p>
          </div>
        ) : (
          filteredItems.map((item, index) => {
            const displayName = item.displayName;
            const locationName = item.location.location || item.location._id || 'Unknown Location';
            // Try to get address from various possible fields
            const lat = typeof item.location.latitude === 'number' ? item.location.latitude : null;
            const lng = typeof item.location.longitude === 'number' ? item.location.longitude : null;
            const address = (item.location as any).address || 
                           (item.location as any).street || 
                           (item.location as any).fullAddress ||
                           (lat !== null && lng !== null
                             ? `${lat.toFixed(4)}, ${lng.toFixed(4)}`
                             : '');
            const distance = item.distance ? formatDistance(item.distance) : null;
            const subtitle = item.type === 'shop' 
              ? locationName 
              : address;

            return (
              <motion.div
                key={`${item.type}-${item.location._id}-${item.shopName || ''}-${index}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.02 }}
                className="brand-locations-item"
                onClick={() => handleItemClick(item)}
              >
                <div className="brand-locations-item-icon">
                  <BrandIcon size={24} />
                </div>
                <div className="brand-locations-item-content">
                  <h3 className="brand-locations-item-name">{displayName}</h3>
                  {subtitle && (
                    <p className="brand-locations-item-address">{subtitle}</p>
                  )}
                </div>
                <div className="brand-locations-item-right">
                  {distance && (
                    <span className="brand-locations-item-distance">{distance}</span>
                  )}
                  <ChevronRight size={20} className="brand-locations-item-arrow" />
                </div>
              </motion.div>
            );
          })
        )}
      </div>
    </motion.div>
  );
};

export default BrandLocationsView;

